<template>
    <div class="file-upload-component">
        <el-upload ref="uploadRef" class="hidden-upload" action="#" :multiple="multiple" :accept="acceptType"
            :on-change="handleFileChange" :on-exceed="handleExceed" :auto-upload="false" :file-list="fileList">
            <slot name="upload-btn">
                <el-button type="primary" @click="triggerUpload">选择文件上传</el-button>
            </slot>
        </el-upload>
    </div>
</template>

<script>
import axios from 'axios'
import COS from 'cos-js-sdk-v5'
import { getPolicyTxCloudEquityAjTools } from '../../api/api'

export default {
    name: 'uploadOnly',
    props: {
        llmUploadApi: { type: String, required: true },
        multiple: { type: Boolean, default: true },
        acceptType: { type: String, default: '' },
        llmHeaders: { type: Object, default: () => ({}) },
        limit: { type: Number, default: Infinity },
        // 接收父组件已上传的文件列表（用于判断重复）
        historyFiles: { type: Array, default: () => [] }
    },
    data() {
        return {
            fileList: [], // 本次选择的文件
            loadings: null,
            cosInstance: null,
            // 子组件内部维护完整的文件数组（历史+本次）
            allFiles: []
        }
    },
    watch: {
        // 监听父组件传入的历史文件列表，同步到子组件内部
        historyFiles: {
            immediate: true, // 初始化时立即同步
            deep: true,
            handler(newVal) {
                this.allFiles = [...newVal] // 同步父组件的历史数据到子组件
            }
        }
    },
    methods: {

        // ========== 核心新增：全局文件去重方法（按云URL/文件名+大小） ==========
        uniqueFiles(fileList) {
            const map = new Map()
            // 优先按腾讯云URL去重，无URL则按文件名+大小
            fileList.forEach(file => {
                const key = file.url || `${file.name}_${file.raw.size}`
                if (!map.has(key)) {
                    map.set(key, file)
                }
            })
            return Array.from(map.values())
        },

        // 生成绝对唯一的文件ID（修复ID碰撞问题）
        generateUniqueId() {
            // 时间戳 + 随机数 + 微秒数，杜绝重复
            return `${Date.now()}_${performance.now()}_${Math.random().toString(36).substr(2, 9)}`
        },

        resetUploadState() {
            if (this.loadings) {
                this.loadings.close()
                this.loadings = null
            }
            this.fileList = []
            this.cosInstance = null
            const fileInput = this.$refs.uploadRef?.$el.querySelector('input[type="file"]')
            if (fileInput) fileInput.value = ''
        },

        triggerUpload() {
            this.$refs.uploadRef.$el.querySelector('input[type="file"]').click()
        },

        // 单个文件重复校验
        checkFileDuplicate(file) {
            return this.allFiles.some(item => {
                // 优先按云URL判断，无URL则按文件名+大小
                if (item.url) {
                    return item.url === (file.url || '') || (item.name === file.name && item.raw.size === file.raw.size)
                }
                return item.name === file.name && item.raw.size === file.raw.size
            })
        },

        handleFileChange(file, fileList) {
            if (file.status !== 'ready') return

            // 1. 过滤当前选择列表中的重复文件
            const filteredList = fileList.filter(item => !this.checkFileDuplicate(item))
            this.fileList = filteredList

            // 2. 批量上传过滤后的文件
            this.handleBatchUpload()
        },

        async handleBatchUpload() {
            if (this.fileList.length === 0) return

            const newUploadedFiles = [] // 存储本次上传成功的文件
            for (const fileItem of this.fileList) {
                // 二次校验：防止循环中数据变化
                if (this.checkFileDuplicate(fileItem)) {
                    this.$message.warning(`文件【${fileItem.name}】已上传，跳过`)
                    continue
                }

                try {
                    // 腾讯云上传
                    const cosResult = await this.TenXunCloudUploadFile(fileItem.raw)
                    const cloudUrl = cosResult.fileurl

                    // 三次校验：按实际云URL最终确认
                    const urlDuplicate = this.allFiles.some(item => item.url === cloudUrl)
                    if (urlDuplicate) {
                        this.$message.warning(`文件【${fileItem.name}】已上传到腾讯云，跳过`)
                        continue
                    }

                    // 构建文件数据（使用新的唯一ID生成方法）
                    const fileId = this.generateUniqueId()
                    const fileName = fileItem.name
                    const extension = fileName.split('.').pop().toLowerCase()
                    const mime_type = fileItem.raw.type
                    let type = 'document'
                    if (mime_type.startsWith('image/')) type = 'image'
                    if (mime_type.startsWith('video/')) type = 'video'
                    if (mime_type.startsWith('audio/')) type = 'audio'
                    if (mime_type.startsWith('application/')) type = 'document'
                    const fileData = {
                        id: fileId,
                        name: fileName,
                        extension: extension,
                        mime_type: mime_type,
                        type: type,
                        raw: fileItem.raw,
                        transfer_method: 'remote_url',
                        url: cloudUrl, // 腾讯云/移动云地址（核心判断依据）
                        // llmUrl: ''
                    }
                    // 3. 大模型服务上传
                    const llmResult = await this.uploadToLlmServer(fileItem.raw, fileData)
                    fileData.llmUrl = llmResult.data || ''
                    newUploadedFiles.push(fileData)
                } catch (error) {
                    this.$message.error(`文件 ${fileItem.name} 上传失败：${error.message}`)
                }
            }

            // ========== 核心修复：合并后强制去重 ==========
            // 1. 合并历史文件 + 本次新文件
            const tempAllFiles = [...this.allFiles, ...newUploadedFiles]
            // 2. 强制去重，确保无重复项
            this.allFiles = this.uniqueFiles(tempAllFiles)
            // 3. 回传给父组件去重后的完整数组
            this.$emit('update:historyFiles', [...this.allFiles])
            this.$emit('upload-success', [...this.allFiles])

            this.$message.success(`本次上传完成，累计已上传 ${this.allFiles.length} 个唯一文件`)
            this.resetUploadState()
        },

        // 腾讯云/移动云上传逻辑（保持不变）
        getTenXunCloudKey() {
            return new Promise((resolve, reject) => {
                getPolicyTxCloudEquityAjTools().then(res => {
                    res.data ? resolve(res.data) : reject(new Error('获取腾讯云密钥失败'))
                }).catch(err => reject(err))
            })
        },
        getCosInIt() {
            if (this.cosInstance) return this.cosInstance
            const cos = new COS({
                SimpleUploadMethod: 'putObject',
                getAuthorization: async (options, callback) => {
                    try {
                        const data = await this.getTenXunCloudKey()
                        callback({
                            TmpSecretId: data.tmpSecretId,
                            TmpSecretKey: data.tmpSecretKey,
                            SecurityToken: data.sessionToken,
                            StartTime: data.startTime,
                            ExpiredTime: data.expiredTime,
                        })
                    } catch (error) {
                        callback(null, error)
                    }
                }
            })
            this.cosInstance = cos
            return cos
        },
        async TenXunCloudUploadFile(file) {
            return new Promise(async (resolve, reject) => {
                try {
                    const key = await this.getTenXunCloudKey()
                    const cos = await this.getCosInIt()
                    const fileTypeArr = file.name.split('.')
                    const fileName = fileTypeArr[0]
                    const extension = fileTypeArr[fileTypeArr.length - 1]

                    cos.uploadFile({
                        Bucket: key.bucket,
                        Region: key.region,
                        Key: `${key.filePath}/${fileName}.${extension}`,
                        Body: file,
                        SliceSize: 1024 * 1024 * 5,
                        onProgress: (progressData) => {
                            const loadingText = `上传中...${(progressData.percent * 100).toFixed(1)}%`
                            this.loadings ? this.loadings.setText(loadingText) : (
                                this.loadings = this.$loading({
                                    lock: true, text: loadingText, spinner: "el-icon-loading", background: "rgba(0, 0, 0, 0.7)"
                                })
                            )
                        },
                    }).then(data => {
                        this.loadings?.close()
                        this.loadings = null
                        resolve({ fileName: file.name, fileurl: `${key.baseHttp}${key.filePath}/${fileName}.${extension}` })
                    }).catch(err => {
                        this.loadings?.close()
                        this.loadings = null
                        reject(new Error(`腾讯云上传失败：${err.message || '未知错误'}`))
                    })
                } catch (error) {
                    this.loadings?.close()
                    this.loadings = null
                    reject(error)
                }
            })
        },

        // 大模型上传逻辑（保持不变）
        uploadToLlmServer(file, fileInfo) {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('fileName', fileInfo.name)
            formData.append('fileType', fileInfo.type)
            return axios({
                url: this.llmUploadApi,
                method: 'post',
                headers: { 'Content-Type': 'multipart/form-data', ...this.llmHeaders },
                data: formData,
                timeout: 30000
            })
        },

        // 文件超限提示
        handleExceed(files, fileList) {
            this.$message.warning(`最多只能上传 ${this.limit} 个文件，本次选择了 ${files.length} 个，已超出限制`)
        },

        // 子组件提供重置方法（供父组件切换智能体时调用）
        resetAllState() {
            this.allFiles = []
            this.resetUploadState()
            // 重置后通知父组件清空
            this.$emit('update:historyFiles', [])
        }
    },
    beforeDestroy() {
        this.loadings?.close()
    }
}
</script>

<style scoped>
.hidden-upload {
    display: none;
}
</style>